import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ConnectedFormContext, { IConnectedFormModalProps } from '../../components/Context/ConnectedFormContext';
import ModalContext from '../../components/Context/ModalContext';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { IAfterPostSave, IAfterSubmit } from '../../components/connectedForm/useManagedSubmit';
import { ActionType, IMetaAction, IStockAdjustment, Maybe } from '../../graphql/schema/p4types';
import { useDocumentConfig } from '../../utils/FetchConfig';
import StockAdjustmentLabel from '../../graphql/entities/product/StockAdjustmentLabel';

//Used to sort the actions so we process them in the expected order.
const STOCK_ADJUSTMENT_MODAL_MAP: { [key in ActionType]?: number } = {
  [ActionType.PrintStockAdjustmentLabels]: 1,
};

const actionComparator: (a: IMetaAction, b: IMetaAction) => number = (a, b) => {
  const aOrder = (a.actionType && STOCK_ADJUSTMENT_MODAL_MAP[a.actionType]) || -1;
  const bOrder = (b.actionType && STOCK_ADJUSTMENT_MODAL_MAP[b.actionType]) || -1;
  return aOrder - bOrder;
};

const StockAdjustmentLabelModal = ({
  modalOpen,
  metaAction,
  delayBeforeSkip,
}: IConnectedFormModalProps & {
  delayBeforeSkip: boolean;
}) => {
  const connectedForm = useContext(ConnectedFormContext);

  return (
    <DocumentModal2
      modalOpen={modalOpen}
      metaAction={metaAction}
      {...(connectedForm as any)}
      key={'stockAdjustmentLabels'}
      entity={StockAdjustmentLabel}
      allowEntityEmail={false}
      delayBeforeSkip={delayBeforeSkip}
      allowEmailNoDefaultRecipient={true}
    />
  );
};

type IPostSaveModalState = { action?: IExtendedMetaAction };

const StockAdjustmentPostSaveModal = ({
  postSaveModalState,
  isFirstAction,
  onCompleteAction,
}: {
  postSaveModalState: IPostSaveModalState;
  isFirstAction: boolean;
  onCompleteAction: (closeModals: boolean) => void;
}) => {
  const documentConfig = useDocumentConfig();

  let modal;
  //This if statement is required so we don't re-process this when we 'clear' the postSaveModalState (I.e. on close of a modal).
  if (postSaveModalState.action?.actionType) {
    switch (postSaveModalState.action.actionType) {
      case ActionType.PrintStockAdjustmentLabels:
        if (documentConfig?.stockAdjustmentLabelDocumentDefault?.documentsInUse || true) {
          modal = (
            <StockAdjustmentLabelModal
              modalOpen={true}
              metaAction={postSaveModalState.action}
              delayBeforeSkip={!isFirstAction}
            />
          );
        }

        break;
      default:
        onCompleteAction(false);
    }
  }

  const modalContext = useMemo(() => ({ onClose: onCompleteAction }), [onCompleteAction]);
  return <ModalContext.Provider value={modalContext}>{modal}</ModalContext.Provider>;
};

export type IExtendedMetaAction = IMetaAction & {
  postSave?: boolean; //Indicates if this was triggered by entityMetaData as a postSave or otherwise.
  preSave?: boolean; //Indicates this is being triggered immediately prior to completion (Trade Counter).
  options?: {
    rowIdx?: number;
  };
};

/**
 * Handler for post-save behaviours for SalesOrderForm.
 *
 * Separated from ConnectedForm handling in P4-6142 to make it easier for us to manipulate and change the order of display of elements
 * and to make it easier for us to check modal states by other Sales Order logic (E.g. Avoid a 'line' triggered modal from staying open
 * if postSave modals have been triggered).
 */
export const useProductsStockAdjustmentPostSave: () => {
  postSaveFunction: IAfterPostSave;
  modals: ReactElement;
  addModalActions: (actions: Array<IExtendedMetaAction>) => void;
} = () => {
  const [afterPostSave, setAfterPostSave] = useState<IAfterSubmit | undefined>();
  const [result, setResult] = useState<any>(); //Just used to hold the result for passing through to afterPostSave later.

  const [modalState, setModalState] = useState<IPostSaveModalState>({});
  const [actions, setActions] = useState<Array<IExtendedMetaAction>>([]);
  const [nextActionIndex, setNextActionIndex] = useState(-1);

  const onCompleteAction = useCallback((closeModals: boolean = true) => {
    if (closeModals) {
      setModalState({});
    }
    setNextActionIndex((actionIndex) => actionIndex + 1);
  }, []);

  useEffect(() => {
    if (nextActionIndex >= 0) {
      if (nextActionIndex < actions.length) {
        const action = actions[nextActionIndex];
        setModalState({ action });
      } else {
        setNextActionIndex(-1);
        afterPostSave?.({ result });
      }
    }
    //Very explicitly only want to run this when the index changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextActionIndex]);

  const stockAdjustmentPostSave: IAfterPostSave = useCallback((afterPostSave, { result }) => {
    //Note - Since we're setting a function into state, need to have the function wrapper like this, or it see's it as a state
    //     - setter and calls the function immediately, which is not the intent here.
    setAfterPostSave(() => afterPostSave);
    setResult(result);

    const entityMetaData = (result as Maybe<IStockAdjustment>)?.entityMetaData;

    if (entityMetaData?.actions && Array.isArray(entityMetaData.actions) && entityMetaData.actions.length > 0) {
      const newActions = (entityMetaData.actions.filter((action) => !!action?.actionType) as Array<IMetaAction>)
        .sort(actionComparator)
        .map((action) => ({ ...action, postSave: true }));

      setActions(newActions);
      setNextActionIndex(0);
    } else {
      //Nothing to process here right now, continue on to 'afterSubmit' logic.
      afterPostSave?.({ result });
    }
  }, []);

  //Allow adding of actions via non-save means (Allow batch allocation/shortages to use the same modals as post-save by setting actions).
  const addModalActions = (actions: Array<IMetaAction>) => {
    setActions(actions);
    setNextActionIndex(0);
    setAfterPostSave(undefined); //Clear 'afterPostSave' so we don't accidentally navigate or similar when using this capability.
  };

  //Memoising result to be sure we don't cause any more renders than necessary.
  return useMemo(() => {
    return {
      postSaveFunction: stockAdjustmentPostSave,
      modals: (
        <StockAdjustmentPostSaveModal
          key={'postSaveModal'}
          postSaveModalState={modalState}
          isFirstAction={nextActionIndex === 0}
          onCompleteAction={onCompleteAction}
        />
      ),
      addModalActions,
    };
  }, [stockAdjustmentPostSave, modalState, nextActionIndex, onCompleteAction]);
};
